import axios from 'axios';
import Router from '@/router';
import Cookies from 'js-cookie';
import { camelizeKeys, haveProperty } from '@/utils/';

// definição dos valores defaults. Para além disso
// é feita a validação, se já existe um utilizador autenticado
//     caso exista, é então adicionado o `token` gerado pela API aos headers.
const instance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    headers: {
        common: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
            'Access-Control-Allow-Origin': process.env.VUE_APP_URL,
            'Accept-Language': 'pt-PT',
            ...( Cookies.get( 'token' ) && {
                Authorization: `bearer ${Cookies.get( 'token' )}`
            })
        }
    }
});

/**
 *
 * quando é realizado um pedido novo, ocorre uma validação se
 * os pedidos anteriormente realizados foram concluídos.
 *     caso não tenham, são cancelados, de forma a não haver conflito entre informação.
 *
 * para mais informações sobre o assunto, ler os seguintes urls:
 * - https://github.com/axios/axios#cancellation
 * - https://medium.com/@wikycheng/handle-duplicate-ajax-request-and-abort-the-pending-previous-http-call-when-route-changing-using-3706d769f9e6
 * - https://blog.andrewjamesbibby.com/2019/08/01/cancel-all-pending-axios-requests-in-vue-spa-on-router-change/
 *
*/
const pendingRequests = {};
const { CancelToken } = axios;
const removeRequestFromPending = ({ config, token }) => {
    // certifica de que o url é o mesmo para o pedido e resposta.
    const url = config.url.replace( config.baseURL, '/' );

    // stringify o pedido com método.
    const urlWithMethod = `${url}&${config.method}`;

    // valida se o url está nos pendentes
    //     caso esteja, é apagado.
    //     caso não esteja é adicionado.
    ( urlWithMethod in pendingRequests )
        ? delete pendingRequests[urlWithMethod]
        : pendingRequests[urlWithMethod] = token;
};

// interceptors - request
instance.interceptors.request.use( config => {
    // armazena o canceltoken nos pedidos pendentes.
    config.cancelToken = new CancelToken( c => {
        removeRequestFromPending({ config, token: c });
    });

    return config;
}, error => {
    Promise.reject( error );
});

instance.interceptors.response.use(
    response => {
        // apaga o url dos pedidos pendentes.
        removeRequestFromPending({ config: response.config });

        // Se o pedido a API for para obter um ficheiro ( audio, pdf, etc ), então
        // não irá ter content, como tal não é necessário transformar as keys.
        // Caso seja um pedido normal, transforma as keys da resposta em camelCase.
        if ( haveProperty( response.data, 'content' ) )
            response.data.content = camelizeKeys( response.data.content );

        return response;
    },
    async error => {
        const originalRequest = error.config;
        const { status, data } = error.response;

        if ( haveProperty( data.content, 'errors' ) )
            data.content.errors = camelizeKeys( data.content.errors );

        const visitedPage = Router.history.current.path.toLowerCase();

        switch ( status ) {
            case 401:
                // valida se o erro ocorrido foi numa página de autenticação
                // ou se o pedido a API é de refresh token
                //    caso seja, então não irá executar o código abaixo.
                if ( visitedPage.includes( 'authentication' )
                    || originalRequest.url.includes( '/auth/refresh-token' )
                )
                    return;

                // é feito o pedido API, de forma a actualizar o token.
                try {
                    // faz o pedido obter o novo token
                    const response = await instance.get( '/auth/refresh-token' );
                    const { accessToken, tokenType } = response.data.content.data;

                    // guarda o novo token nas cookies,
                    // adiciona o novo token nos headers do axios e no pedido anterior.
                    Cookies.set( 'token', accessToken );
                    instance.defaults.headers.common.Authorization = `${tokenType} ${accessToken}`;
                    originalRequest.headers.Authorization = `${tokenType} ${accessToken}`;

                    // executa o pedido anterior
                    // eslint-disable-next-line
                    return instance.request( originalRequest );
                } catch ( errorCatch ) {
                    // ocorreu erro ao fazer refresh ao token
                    // como tal, é limpado as cookies e
                    // o utilizador é direccionado para a página de login
                    Cookies.remove( 'currentUser' );
                    Cookies.remove( 'token' );
                    Router.go({ name: 'login', query: { redirect: visitedPage } });
                }
                break;
            case 403: // 403 - não tem permissão para entrar na rota.
                Router.push({ name: 'notFound', params: { 0: visitedPage } });
                break;
            default:
                break;
        }

        removeRequestFromPending({ config: error.config });
        // eslint-disable-next-line
        return !axios.isCancel( error ) ? Promise.reject( data ) : Promise.resolve({ }); // return empty object for aborted request
    }
);

// eslint-disable-next-line
instance.clear = () => {
    Object.keys( pendingRequests ).forEach( key => {
        if ( pendingRequests[key] ) {
            pendingRequests[key]();
            delete pendingRequests[key];
        }
    });
};

export default instance;